import React from "react";
import styled from "styled-components";
import {
  PatternGeneration,
  PATTERN_BAR_OPTIONS,
  PatternBarOption,
} from "../types";
import DropdownSelector from "./DropdownSelector";

interface PatternGenerationControlsProps {
  id?: string;
  config: PatternGeneration;
  onChange: (config: PatternGeneration) => void;
  disabled?: boolean;
}

// Display labels for bar options
const BAR_LABELS: Record<PatternBarOption, string> = {
  0.25: "1/4",
  0.5: "1/2",
  1: "1",
  2: "2",
  4: "4",
  8: "8",
};

// Generate steps options (1-16)
const STEPS_OPTIONS = Array.from({ length: 16 }, (_, i) => i + 1);

// Generate bars options
const BARS_OPTIONS = PATTERN_BAR_OPTIONS.map((bar) => ({
  value: bar,
  label: BAR_LABELS[bar],
}));

const PatternGenerationControls: React.FC<PatternGenerationControlsProps> = ({
  id = "pattern-generation-controls",
  config,
  onChange,
  disabled = false,
}) => {
  // Handle step count change
  const handleStepChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (disabled) return;
    const steps = parseInt(e.target.value, 10);
    // Ensure steps are within valid range (1-16)
    const clampedSteps = Math.min(Math.max(steps, 1), 16);
    onChange({ ...config, steps: clampedSteps });
  };

  // Handle bar selection
  const handleBarChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (disabled) return;
    const bar = parseFloat(e.target.value);
    const validBar = PATTERN_BAR_OPTIONS.find((b) => b === bar);
    if (validBar !== undefined) {
      onChange({ ...config, bars: validBar });
    }
  };

  // Prepare options for dropdowns
  const stepsOptions = STEPS_OPTIONS.map((step) => ({
    key: step.toString(),
    label: step.toString(),
  }));

  const barsOptions = BARS_OPTIONS.map((bar) => ({
    key: bar.value.toString(),
    label: `${bar.label} bar${bar.value !== 1 ? "s" : ""}`,
  }));

  return (
    <ControlsContainer id={id}>
      {/* Steps Control */}
      <DropdownWithIconWrapper id={`${id}-steps`}>
        <DropdownSelector
          id={`${id}-steps-dropdown`}
          value={config.steps.toString()}
          options={stepsOptions}
          onChange={handleStepChange}
          disabled={disabled}
          minWidth="60px"
          showBorder={false}
        />
        <StepsIcon>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.08464 4.1665H0.417969V8.33317H2.08464V4.1665Z"
              fill="#CFC9C9"
            />
            <path
              d="M4.58464 1.6665H2.91797V8.33317H4.58464V1.6665Z"
              fill="#CFC9C9"
            />
            <path d="M7.08464 5H5.41797V8.33333H7.08464V5Z" fill="#CFC9C9" />
            <path
              d="M9.58464 3.3335H7.91797V8.3335H9.58464V3.3335Z"
              fill="#CFC9C9"
            />
          </svg>
        </StepsIcon>
      </DropdownWithIconWrapper>

      {/* Bars Control */}
      <DropdownWithIconWrapper id={`${id}-bars`}>
        <DropdownSelector
          id={`${id}-bars-dropdown`}
          value={config.bars.toString()}
          options={barsOptions}
          onChange={handleBarChange}
          disabled={disabled}
          minWidth="60px"
          showBorder={false}
        />
        <BarsIcon>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_1025_44719)">
              <path
                d="M6.66602 8.33301L5.83268 8.33301L5.83268 7.91634L5.41602 7.91634L5.41602 7.49967L4.99935 7.49967L4.99935 7.08301L4.58268 7.08301L4.58268 6.66634L4.16602 6.66634L4.16602 6.24968L3.74935 6.24968L3.74935 5.83301L3.33268 5.83301L3.33268 5.41634L2.91602 5.41634L2.91602 4.58301L3.33268 4.58301L3.33268 4.16634L3.74935 4.16634L3.74935 3.74968L4.16602 3.74968L4.16602 3.33301L4.58268 3.33301L4.58268 2.91634L4.99935 2.91634L4.99935 2.49968L5.41602 2.49968L5.41602 2.08301L5.83268 2.08301L5.83268 1.66634L6.66602 1.66634L6.66602 2.49968L6.24935 2.49968L6.24935 2.91634L5.83268 2.91634L5.83268 3.33301L5.41602 3.33301L5.41602 3.74968L4.99935 3.74968L4.99935 4.16634L4.58268 4.16634L4.58268 4.58301L9.58268 4.58301L9.58268 5.41634L4.58268 5.41634L4.58268 5.83301L4.99935 5.83301L4.99935 6.24968L5.41602 6.24968L5.41602 6.66634L5.83268 6.66634L5.83268 7.08301L6.24935 7.08301L6.24935 7.49967L6.66602 7.49967L6.66602 8.33301Z"
                fill="#CFC9C9"
              />
              <path
                d="M1.25 9.16671L1.25 0.833374L0.416667 0.833374L0.416667 9.16671L1.25 9.16671Z"
                fill="#CFC9C9"
              />
            </g>
            <defs>
              <clipPath id="clip0_1025_44719">
                <rect
                  width="10"
                  height="10"
                  fill="white"
                  transform="translate(10) rotate(90)"
                />
              </clipPath>
            </defs>
          </svg>
        </BarsIcon>
      </DropdownWithIconWrapper>
    </ControlsContainer>
  );
};

export default PatternGenerationControls;

// Styled Components
const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DropdownWithIconWrapper = styled.div`
  position: relative;
  display: inline-block;

  select {
    padding-left: 18px !important;
  }
`;

const StepsIcon = styled.div`
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cfc9c9;
  pointer-events: none;
  z-index: 1;

  svg {
    display: block;
    width: 12px;
    height: 12px;
  }
`;

const BarsIcon = styled.div`
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cfc9c9;
  pointer-events: none;
  z-index: 1;

  svg {
    display: block;
    width: 12px;
    height: 12px;
  }
`;
